import { CollapsedFieldRow } from '@liza/ui/components/timing/CollapsedFieldRow';
import { Button } from '@liza/ui/components/ui/button';
import { Field, FieldContent, FieldGroup, FieldLabel } from '@liza/ui/components/ui/field';
import {
  isRecurringScheduleType,
  sortWhenCodes,
  WHEN_CODE_ORDER,
  type ScheduleType,
  type TimeOfDayCode,
} from '@liza/ui/utils/schedulePicker';
import { useTranslation } from 'react-i18next';

const CHIP_LABEL_KEY: Record<TimeOfDayCode, string> = {
  MORN: 'whenChip.morning',
  AFT: 'whenChip.afternoon',
  EVE: 'whenChip.evening',
  NIGHT: 'whenChip.bedtime',
};

/**
 * Time-of-day chips. On Days of week they multi-select — N chips = N times
 * per day, the only way the picker expresses per-day multiplicity. On the
 * days-per-week and Every… modes a single chip applies as the canonical
 * annotation, so selecting one replaces the other.
 */
export function TimeOfDayRow({
  scheduleType,
  hourly,
  whenCodes,
  onChange,
}: {
  scheduleType: ScheduleType;
  hourly: boolean;
  whenCodes: TimeOfDayCode[];
  onChange: (codes: TimeOfDayCode[]) => void;
}): React.ReactNode {
  const { t } = useTranslation('common');
  // Hourly never carries a time-of-day annotation, so the row hides rather
  // than letting a chip be selected only for the writer to discard it.
  if (!isRecurringScheduleType(scheduleType) || hourly) return null;
  const multi = scheduleType === 'simple';
  const toggle = (code: TimeOfDayCode): void => {
    if (whenCodes.includes(code)) {
      onChange(whenCodes.filter((c) => c !== code));
    } else {
      onChange(multi ? sortWhenCodes([...whenCodes, code]) : [code]);
    }
  };
  return (
    <CollapsedFieldRow
      name="time-of-day"
      label={t('timeOfDayLabel')}
      hasValue={whenCodes.length > 0}
      onClear={() => onChange([])}
    >
      <FieldGroup>
        <Field>
          <FieldLabel>{t('timeOfDayLabel')}</FieldLabel>
          <FieldContent className="flex flex-row gap-1">
            {WHEN_CODE_ORDER.map((code) => (
              <Button
                key={code}
                type="button"
                size="xs"
                className="flex-1"
                variant={whenCodes.includes(code) ? 'default' : 'secondary'}
                aria-pressed={whenCodes.includes(code)}
                data-testid={`time-of-day-${code}`}
                onClick={() => toggle(code)}
              >
                {t(CHIP_LABEL_KEY[code])}
              </Button>
            ))}
          </FieldContent>
        </Field>
      </FieldGroup>
    </CollapsedFieldRow>
  );
}
